<template>
  <MapLayout>
    <template slot="content">
      <div class="main-content-cta">
        <HeaderText tag="h1">{{ header }}</HeaderText>
        <p v-if="description">{{ description }}</p>
        <div class="primary-actions">
          <BaseButton
            v-for="{ text, ...rest } in primaryActions"
            :key="text"
            v-bind="rest"
            class="primary-action"
          >
            {{ text }}
          </BaseButton>
        </div>
      </div>
      <div class="secondary-actions" v-if="secondaryActions.length">
        <RouterLink
          v-for="{ text, ...rest } in secondaryActions"
          :key="text"
          v-bind="rest"
          class="secondary-action"
        >
          {{ text }}
        </RouterLink>
        <SocialSharing class="secondary-action" />
      </div>
    </template>
    <template slot="map">
      <div class="map-control">
        <SmallHeader tag="h1" class="map-title">Explore</SmallHeader>
        <div class="resource-prefix">COVID-19 Hospital Status for</div>
        <div class="resource-selector">
          <div class="select-container">
            <select v-model="selectedResource" class="resource-selector-input">
              <option
                v-for="{ label, value } in resourceOptions"
                :key="value"
                :value="value"
              >
                {{ label }}
              </option>
            </select>
            <CaretDown class="caret-down" />
          </div>
        </div>
      </div>
      <MapLegend />
      <HospitalMap class="map" :resource="selectedResource" />
    </template>
  </MapLayout>
</template>

<script>
import HospitalMap from '@/components/HospitalMap';
import BaseButton from '@/components/base-button';
import CaretDown from '@/assets/caret-down.svg';
import HeaderText from '@/components/header-text';
import MapLayout from '@/components/map-layout';
import MapLegend from '@/components/map-legend';
import SmallHeader from '@/components/small-header';
import SocialSharing from '@/components/social-sharing';
import { HospitalResources, TargetAudiences } from '@/store/types';
import { RouterAliases } from '@/router';

export default {
  components: {
    BaseButton,
    CaretDown,
    HeaderText,
    HospitalMap,
    MapLayout,
    MapLegend,
    SmallHeader,
    SocialSharing,
  },
  props: {
    targetAudience: {
      type: String,
      default: TargetAudiences.DEFAULT,
    },
  },
  data() {
    return {
      selectedResource: 'beds',
    };
  },
  computed: {
    description() {
      switch (this.targetAudience) {
        case TargetAudiences.DOCTOR:
        case TargetAudiences.EMT:
          return null;
        case TargetAudiences.REPORTING:
          return 'Your reports will help us visualize and level the demand on hospitals so together we can flatten the COVID-19 curve.';
        default:
          return 'If you are an EMT, primary care physician or patient, find hospitals near you with capacity. If you are a nurse, doctor, health department or state share locations with capacity below.';
      }
    },
    header() {
      switch (this.targetAudience) {
        case TargetAudiences.DOCTOR:
        case TargetAudiences.EMT:
          return 'If you’re an EMT or Doctor looking for Urgent Care for your Patient:';
        case TargetAudiences.REPORTING:
          return 'We’re looking for reports from Doctors, Nurses, Hospitals and the Public.';
        default:
          return 'Help level demand on our health care system and front-line workers today';
      }
    },
    primaryActions() {
      switch (this.targetAudience) {
        case TargetAudiences.DOCTOR:
        case TargetAudiences.EMT:
          return [
            {
              tag: 'RouterLink',
              to: {
                name: RouterAliases.DOCTOR_ASSESSMENT,
                params: { step: 1 },
              },
              variant: 'primary',
              text: 'Hospital search',
            },
            {
              tag: 'RouterLink',
              to: { name: RouterAliases.HOSPITAL_SEARCH },
              text: 'Report on a hospital’s status',
            },
          ];
        case TargetAudiences.REPORTING:
          return [
            {
              tag: 'RouterLink',
              to: { name: RouterAliases.HOSPITAL_SEARCH },
              variant: 'primary',
              text: 'Report on a hospital’s status',
            },
            // {
            //   tag: 'RouterLink',
            //   to: { name: RouterAliases.PATIENT_HOME },
            //   text: 'Find a hospital now',
            // },
          ];
        default:
          return [
            {
              tag: 'RouterLink',
              to: { name: RouterAliases.FLOW_PICKER },
              variant: 'primary',
              text: 'Find a hospital now',
            },
            {
              tag: 'RouterLink',
              to: { name: RouterAliases.HOSPITAL_SEARCH },
              text: 'Report on a hospital’s status',
            },
          ];
      }
    },
    resourceOptions() {
      return Object.entries(HospitalResources).map(([k, v]) => ({
        value: k.toLowerCase(),
        label: v + 's',
      }));
    },
    secondaryActions() {
      return [
        {
          text: 'Read the FAQs',
          to: {
            name: RouterAliases.FAQ,
          },
        },
        {
          text: 'Contact Us',
          to: {
            name: RouterAliases.CONTACT,
          },
        },
      ];
    },
  },
};
</script>

<style lang="scss" scoped>
.main-content-cta {
  margin: auto 0;
}

.header-text {
  margin-bottom: 16px;
}

.primary-actions {
  margin: 24px 0;
}

.primary-action + .primary-action {
  margin-top: 12px;
}

.map {
  bottom: 0;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
  width: 100%;
}

.map-control {
  align-items: center;
  display: flex;
  font-size: 26px;
  flex-direction: column;
  line-height: 30px;
  position: relative;
  z-index: 1;
}

.small-header {
  font-size: 12px;
}

.map-title {
  margin-bottom: 12px;
}

.secondary-action {
  align-items: center;
  border-top: 1px solid var(--white-transparent-lighter);
  color: var(--white);
  display: flex;
  justify-content: space-between;
  padding: 12px 24px 12px 0;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
}

.resource-prefix {
  font-weight: 500;
}

.resource-selector {
  font-weight: bold;
  margin-top: 5px;
}

.select-container {
  background-color: var(--white-transparent);
  border-radius: 10px;
  position: relative;
  transition: 0.3s ease background;

  &:hover {
    background-color: var(--white);
  }
}

.resource-selector-input {
  cursor: pointer;
  padding: 10px 30px 5px 15px;
  position: relative;
  z-index: 1;
}

.caret-down {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 0;
}

.btn-icon {
  margin-left: 24px;
}

.map-legend {
  display: none;

  @media screen and (min-width: 768px) {
    bottom: var(--page-padding);
    display: flex;
    left: 50%;
    position: absolute;
    transform: translateX(-50%);
    z-index: 1;
  }
}
</style>
